import styled from "styled-components";
import { useContext } from "react";
import { WidgetContext } from "../../widgetContext";
import { WidgetSection } from "./widgetSection";
import LastUpdatedFooter from "../../widgetLastUpdatedFooter";
import WidgetLoading from "../../widgetLoading";
import { format } from "date-fns";

const SmallTwinNumberWidget = () => {
  const widget = useContext(WidgetContext);

  const handleWidgetOnClick = () => {
    if (widget.widgetOnClick) {
      widget.widgetOnClick();
    }
  };

  // Check if widget data has the required structure (not just type/title from schema)
  const hasValidData = widget.widgetData?.data?.section_1 && widget.widgetData?.data?.section_2;

  const leftSectionConfig = hasValidData ? widget.widgetData.data.section_1 : null;
  const rightSectionConfig = hasValidData ? widget.widgetData.data.section_2 : null;

  // Show loading state while data is being fetched
  if (widget.widgetLoading || !hasValidData) {
    return (
      <Wrapper className={widget.isTimelessWidget ? "outline" : ""}>
        <WidgetLoading />
      </Wrapper>
    );
  }

  return (
    <Wrapper
      className={`${widget.widgetData.variant || ""} ${widget.isTimelessWidget ? "outline" : ""}`}
      onClick={handleWidgetOnClick}
    >
      {hasValidData ? (
        <>
          <div className="widget-header">
            <div className="widget-title">{widget.widgetData.title ?? ""}</div>
          </div>
          <div className="widget-info">
            <WidgetSection
              tooltipText={`${leftSectionConfig.pre_text} ${formatNumber(
                parseFloat(leftSectionConfig.value)
              )}`}
              sectionPretext={leftSectionConfig.pre_text}
              sectionText={leftSectionConfig.text}
              sectionDescription={leftSectionConfig.description}
              sectionRawValue={parseFloat(leftSectionConfig.value)}
            />
            <WidgetSection
              tooltipText={`${rightSectionConfig.pre_text} ${formatNumber(
                parseFloat(rightSectionConfig.value)
              )}`}
              sectionPretext={widget.widgetData.data.section_2.pre_text}
              sectionText={rightSectionConfig.text}
              sectionDescription={rightSectionConfig.description}
              sectionRawValue={parseFloat(rightSectionConfig.value)}
            />
          </div>
        </>
      ) : null}
      {widget.isTimelessWidget && widget.lastUpdatedTime ? (
        <LastUpdatedFooter
          date={format(widget.lastUpdatedTime, "do MMMM yyyy")}
          time={format(widget.lastUpdatedTime, "hh:mm aaa")}
        />
      ) : null}
    </Wrapper>
  );
};

export default SmallTwinNumberWidget;

const Wrapper = styled.div`
  border-radius: 20px;
  background-color: ${({ theme }) => theme.widget.background};
  filter: drop-shadow(5px 5px 10px rgba(0, 0, 0, 0.05));
  width: 100%;
  height: 100%;
  padding: 25px;
  display: flex;
  cursor: pointer;
  flex-direction: column;
  justify-content: flex-start;
  gap: 0.5rem;

  .widget-info {
    width: 100%;
    height: 100%;
    display: flex;
  }

  &.danger {
    background-color: #cb2e2e;
    color: #fff;
    .value { color: #fff !important; }
    .currency-label { color: #fff; }
  }

  &.outlined, &.outline {
    border: 10px solid ${({ theme }) => theme.widget.outline};
    color: #0d7459;
    .value { color: #0d7459 !important; }
    .currency-label { color: #0d7459; }
  }

  .widget-header .widget-title {
    display: inline;
    font-size: 26px;
    font-weight: 500;
    opacity: 0.8;
    text-decoration: underline;
  }

  .section {
    display: flex;
    flex-direction: column;
    width: 100%;
    padding-left: 20px;
    justify-content: center;

    &:nth-child(2) {
      padding-left: 40px;
      border-left: ${({ theme }) => theme.partition_line};
    }

    .currency-label {
      font-size: 16px;
      font-weight: 500;
      line-height: 1;
      margin-bottom: 2px;
      color: ${({ theme }) => theme.tooltip.text};
    }

    .value {
      font-size: 4rem;
      line-height: 1.1;
      font-weight: 800;
      letter-spacing: -2px;
      margin-bottom: 8px;
      color: ${({ theme }) => theme.tooltip.text};
    }

    .text {
      font-size: 1.3rem;
      font-weight: 500;
      line-height: 1.5rem;
    }

    .description {
      font-size: 18px;
      font-weight: 400;
      line-height: 23.4px;
    }
  }

  @media (max-width: 995px) {
    padding: 20px;
    gap: 0.3rem;
    .widget-header .widget-title { font-size: 20px; }
    .section {
      padding-left: 12px;
      &:nth-child(2) { padding-left: 20px; }
      .currency-label { font-size: 13px; }
      .value { font-size: 2.5rem; letter-spacing: -1px; }
      .text { font-size: 1rem; line-height: 1.2rem; }
      .description { font-size: 14px; line-height: 1.2; }
    }
  }

  @media (max-width: 767px) {
    padding: 16px;
    border-radius: 16px;
    .widget-header .widget-title { font-size: 16px; }
    .section {
      padding-left: 8px;
      &:nth-child(2) { padding-left: 14px; }
      .currency-label { font-size: 11px; margin-bottom: 1px; }
      .value { font-size: 1.8rem; letter-spacing: -1px; margin-bottom: 4px; }
      .text { font-size: 0.85rem; line-height: 1.1rem; }
      .description { font-size: 12px; line-height: 1.1; }
    }
  }
`;
